import React, { useState } from "react"
import { useParams } from "react-router-dom"

import {
	Box,
	Button,
	Grid,
	IconButton,
	InputAdornment,
	TextField,
	Typography
} from "@material-ui/core"
import { Close as CloseIcon, List as ListIcon, Search as SearchIcon, ShowChart as ShowChartIcon } from "@material-ui/icons"

import { TableColumn } from "@/components/DataTable"
import { Loading, Notification, Portlet } from "@/components"

import {
	TrackableLinkInteraction,
	TrackableLinkInteractionDailySeriesEntry,
	TrackableLinkInteractionResponse,
	TrackableLinkInteractionsHasRespondedFilter,
	TrackableLinkInteractionsSummary
} from "@/protocols/trackableLinks"
import { DEFAULT_WHERE_DATA, DefaultWhereDataType } from "@/utils/pagination"
import useValidation, { ErrorType } from "@/hooks/useValidation"

import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"

import TrackableLinkInteractionsSkeleton from "@/pages/Admin/TrackableLinks/TrackableLinkMetricsV2/TrackableLinkInteractions/TrackableLinkInteractionsSkeleton"
import TrackableLinkInteractionChip from "@/pages/Admin/TrackableLinks/TrackableLinkMetricsV2/TrackableLinkInteractions/TrackableLinkInteractionChip"
import TrackableLinkInteractionClient from "@/pages/Admin/TrackableLinks/TrackableLinkMetricsV2/TrackableLinkInteractions/TrackableLinkInteractionClient"
import InteractionsDailyChart from "@/pages/Admin/TrackableLinks/TrackableLinkMetricsV2/TrackableLinkInteractions/InteractionsDailyChart"

import DataTableWithPagination from "@/components/DataTableWithPagination"

import useStyles from "@/pages/Admin/TrackableLinks/TrackableLinkMetricsV2/TrackableLinkInteractions/styles"

export type TrackableLinkInteractionsProps = {
	fromDate: Date
	toDate: Date
	hasResponded: TrackableLinkInteractionsHasRespondedFilter
	onSummaryChange: (summary: TrackableLinkInteractionsSummary) => void
}

type InteractionsView = "chart" | "table"

const DEFAULT_INTERACTIONS_SUMMARY: TrackableLinkInteractionsSummary = {
	totalClicked: 0,
	totalResponded: 0
}

export type FormattedInteractionsDataResponse = {
	id: number
	client: TrackableLinkInteraction["client"] | null
	clickedAt: Date
	sentMessageAt: Date | null
}

type FormattedInteractionsData = {
	trackableLinkInteractions: FormattedInteractionsDataResponse[]
	count: number
}

const interactionsTableColumns: TableColumn<FormattedInteractionsDataResponse>[] = [
	{ field: "id", label: "Id da interação" },
	{ field: "client", label: "Nome do cliente", rowRender: (value) => <TrackableLinkInteractionClient client={value as TrackableLinkInteraction["client"] } type="nickname" /> },
	{ field: "client", label: "Número do cliente", rowRender: (value) => <TrackableLinkInteractionClient client={value as TrackableLinkInteraction["client"] } type="phone" /> },
	{ field: "clickedAt", label: "Clicou no link", rowRender: (value) => <TrackableLinkInteractionChip interactionDate={value as Date} type="click" /> },
	{ field: "sentMessageAt", label: "Enviou mensagem", rowRender: (value) => <TrackableLinkInteractionChip interactionDate={value as Date} type="sent-message" /> }
]

const TrackableLinkInteractions: React.FC<TrackableLinkInteractionsProps> = ({ fromDate, toDate, hasResponded, onSummaryChange }) => {
	const { trackableLinkId } = useParams<{ trackableLinkId?: string }>()
	const { triggerValidation } = useValidation()

	const [interactionsState, setInteractionsState] = useState({
		interactionsData: { trackableLinkInteractions: [], count: 0 } as FormattedInteractionsData,
		dailySeries: [] as TrackableLinkInteractionDailySeriesEntry[]
	})
	const [loading, setLoading] = useState(false)
	const [paginationState, setPaginationState] = useState<DefaultWhereDataType>(DEFAULT_WHERE_DATA)
	const [search, setSearch] = useState<string | null>(null)
	const [view, setView] = useState<InteractionsView>("chart")

	const classes = useStyles()

	const formatInteractionsData = (data: TrackableLinkInteraction[]): FormattedInteractionsDataResponse[] => {
		const formattedData = data.map((interaction) => {
			return {
				id: interaction.id,
				client: interaction.client ? {
					id: interaction.clientId ?? undefined,
					nickname: interaction.client?.nickname,
					phoneNumber: interaction.client?.phoneNumber
				} : null,
				clickedAt: interaction.clickedAt,
				sentMessageAt: interaction.sentMessageAt
			}
		})

		return formattedData
	}

	const handleGetTrackableLinkInteractions = async (searchOverride?: string | null) => {
		setLoading(true)
		try {
			const searchValue = searchOverride !== undefined ? searchOverride : search

			const hasRespondedFilterToQueryParam: Partial<Record<TrackableLinkInteractionsHasRespondedFilter, boolean>> = {
				responded: true,
				"not-responded": false
			}

			const { data } = await ApiService.get<TrackableLinkInteractionResponse>(`/trackable-links/${trackableLinkId}/interactions`, {
				params: {
					...paginationState,
					search: searchValue,
					dateFrom: fromDate.toISOString(),
					dateTo: toDate.toISOString(),
					hasResponded: hasRespondedFilterToQueryParam[hasResponded]
				}
			})

			const formattedData = formatInteractionsData(data.trackableLinkInteractions)

			setInteractionsState({
				interactionsData: {
					trackableLinkInteractions: formattedData,
					count: data.count
				},
				dailySeries: data.dailySeries ?? []
			})
			onSummaryChange(data.summary ?? DEFAULT_INTERACTIONS_SUMMARY)
		} catch (error) {
			Notification.error({ message: "Ocorreu um erro ao buscar as interações" })

			triggerValidation(error as ErrorType)
			ErrorHandlerService.handle(error as ErrorType)
		} finally {
			setLoading(false)
		}
	}

	const handlePaginationState = (data: Partial<DefaultWhereDataType>) => {
		setPaginationState(lastState => ({
			...lastState,
			...data
		}))
	}

	const handleApplyFilters = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		handlePaginationState({ page: DEFAULT_WHERE_DATA.page })
		await handleGetTrackableLinkInteractions()
	}

	const handleClearSearch = async (): Promise<void> => {
		setSearch("")
		handlePaginationState({ page: DEFAULT_WHERE_DATA.page })
		await handleGetTrackableLinkInteractions("")
	}

	const handleViewChange = (newView: InteractionsView): void => {
		setView(newView)
	}

	React.useEffect(() => {
		handleGetTrackableLinkInteractions()
	}, [paginationState.page, paginationState.rowsPerPage, fromDate.getTime(), toDate.getTime(), hasResponded])

	return (
		<Grid container direction="column" spacing={3}>
			<Grid item>
				<Typography
					variant="h5"
					className={classes.titleText}
				>
					Interações
				</Typography>
			</Grid>

			<Grid item>
				<Loading
					loading={loading}
					customLoadingElement={<TrackableLinkInteractionsSkeleton columnList={interactionsTableColumns} />}
				>
					<Portlet>
						<Grid container direction="column" spacing={2}>
							<Grid item>
								<Grid container justifyContent="space-between" alignItems="center" spacing={1}>
									<Grid item>
										<Box className={classes.viewToggleGroup}>
											<Box
												onClick={() => handleViewChange("chart")}
												className={view === "chart" ? classes.viewToggleTabActive : classes.viewToggleTabInactive}
											>
												<ShowChartIcon fontSize="small" />
												Gráfico
											</Box>
											<Box
												onClick={() => handleViewChange("table")}
												className={view === "table" ? classes.viewToggleTabActive : classes.viewToggleTabInactive}
											>
												<ListIcon fontSize="small" />
												Tabela
											</Box>
										</Box>
									</Grid>
									{view === "table" && (
										<Grid item xs={12} sm="auto">
											<form onSubmit={handleApplyFilters}>
												<Grid container alignItems="center" spacing={1}>
													<Grid item xs={12} sm="auto">
														<TextField
															value={search}
															onChange={
																({ target }) => setSearch(target.value?.trimStart())
															}
															size="small"
															placeholder="Buscar cliente ou número"
															variant="outlined"
															className={classes.search}
															InputProps={{
																startAdornment: (
																	<InputAdornment position="start">
																		<SearchIcon fontSize="small" />
																	</InputAdornment>
																),
																endAdornment: (
																	<InputAdornment position="end">
																		<IconButton
																			size="small"
																			onClick={handleClearSearch}
																		>
																			<CloseIcon fontSize="small" />
																		</IconButton>
																	</InputAdornment>
																)
															}}
														/>
													</Grid>
													<Grid item xs={12} sm="auto">
														<Button
															variant="contained"
															color="primary"
															type="submit"
															className={classes.searchButton}
														>
															Buscar
														</Button>
													</Grid>
												</Grid>
											</form>
										</Grid>
									)}
								</Grid>
							</Grid>
							<Grid item>
								{view === "chart" ? (
									<InteractionsDailyChart dailySeries={interactionsState.dailySeries} />
								) : interactionsState.interactionsData.count === 0 ? (
									<Box className={classes.emptyState}>
										<Typography variant="body2">
											Nenhuma interação encontrada para o período e filtros selecionados
										</Typography>
									</Box>
								) : (
									<DataTableWithPagination
										columns={interactionsTableColumns}
										data={interactionsState.interactionsData.trackableLinkInteractions}
										page={paginationState.page}
										rowsPerPage={paginationState.rowsPerPage}
										totalRows={interactionsState.interactionsData.count}
										onPaginationChange={handlePaginationState}
									/>
								)}
							</Grid>
						</Grid>
					</Portlet>
				</Loading>
			</Grid>
		</Grid>
	)
}

export default TrackableLinkInteractions
